/**
 * Process Type View Component
 * 
 * High-level view of processes grouped by process type with aggregate statistics.
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Button,
  Space,
  message,
  Spin,
  Progress,
  List,
  Divider
} from 'antd';
import {
  ThunderboltOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import { ProcessType } from '../../types/enums';
import styles from './ProcessTypeView.module.css';

interface ProcessTypeSummary {
  type: string;
  count: number;
  complete_count: number;
  incomplete_count: number;
  total_artifacts: number;
  total_transitions: number;
  processes: ProcessInfo[];
}

interface ProcessInfo {
  id: string;
  name: string;
  artifact_count: number;
  is_complete: boolean;
}

interface ProcessTypeViewProps {
  onProcessTypeClick?: (processType: string) => void;
  onProcessClick?: (processId: string) => void;
}

const getProcessTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    [ProcessType.DEFAULT]: 'default',
    [ProcessType.DEMAND]: 'magenta',
    [ProcessType.SUPPLY]: 'blue',
    [ProcessType.DELIVERY]: 'green',
    [ProcessType.FINANCE]: 'gold',
    [ProcessType.HR]: 'purple',
    [ProcessType.IT]: 'cyan',
    [ProcessType.STRATEGY]: 'orange',
    [ProcessType.IMPROVEMENT]: 'lime',
    [ProcessType.QUALITY]: 'green',
    [ProcessType.COMPLIANCE]: 'red',
    [ProcessType.RESEARCH]: 'blue',
    [ProcessType.MARKETFIT]: 'magenta',
    [ProcessType.FITFINDER]: 'purple'
  };
  return colors[type] || 'default';
};

const getProcessTypeDescription = (type: string): string => {
  const descriptions: Record<string, string> = {
    [ProcessType.DEFAULT]: 'Default process type',
    [ProcessType.DEMAND]: 'Processes that identify and capture customer/business needs',
    [ProcessType.SUPPLY]: 'Processes that acquire resources, materials, or services',
    [ProcessType.DELIVERY]: 'Processes that fulfill customer needs and deliver value',
    [ProcessType.FINANCE]: 'Processes that manage financial resources and transactions',
    [ProcessType.HR]: 'Processes that manage human resources and workforce',
    [ProcessType.IT]: 'Processes that manage information technology and systems',
    [ProcessType.STRATEGY]: 'Processes that develop and execute strategic plans',
    [ProcessType.IMPROVEMENT]: 'Processes that enhance and optimize operations',
    [ProcessType.QUALITY]: 'Processes that ensure quality standards and compliance',
    [ProcessType.COMPLIANCE]: 'Processes that ensure regulatory and legal compliance',
    [ProcessType.RESEARCH]: 'Processes that conduct research and development',
    [ProcessType.MARKETFIT]: 'Processes that assess market fit and positioning',
    [ProcessType.FITFINDER]: 'Processes that find and evaluate market opportunities'
  };
  return descriptions[type] || 'Process type';
};

export const ProcessTypeView: React.FC<ProcessTypeViewProps> = ({
  onProcessTypeClick,
  onProcessClick
}) => {
  const [loading, setLoading] = useState(true);
  const [processTypes, setProcessTypes] = useState<ProcessTypeSummary[]>([]);
  const [expandedType, setExpandedType] = useState<string | null>(null);

  useEffect(() => {
    void loadProcessTypes();
  }, []);

  const loadProcessTypes = async () => {
    try {
      setLoading(true);
      
      // Get process types grouped data
      const response = await apiClient.get('/process-types/grouped');
      
      // Transform to our format
      const typeData: ProcessTypeSummary[] = Object.entries(response.data).map(([type, data]: [string, any]) => {
        const processes = data.processes || [];
        const completeCount = processes.filter((p: any) => p.is_complete).length;
        
        return {
          type,
          count: processes.length,
          complete_count: completeCount,
          incomplete_count: processes.length - completeCount,
          total_artifacts: data.total_artifacts || 0,
          total_transitions: data.total_transitions || 0,
          processes: processes.map((p: any) => ({
            id: p.id,
            name: p.name,
            artifact_count: p.artifact_count || 0,
            is_complete: p.is_complete || false
          }))
        };
      });
      
      setProcessTypes(typeData);
    } catch (error: any) {
      message.error(`Failed to load process types: ${error.message}`);
      console.error('Error loading process types:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalProcesses = processTypes.reduce((sum, type) => sum + type.count, 0);
  const totalComplete = processTypes.reduce((sum, type) => sum + type.complete_count, 0);
  const completionPercentage = totalProcesses > 0 ? Math.round((totalComplete / totalProcesses) * 100) : 0;

  return (
    <div className={styles.processTypeView}>
      <Card
        title="Process Type Overview"
        extra={
          <Button onClick={loadProcessTypes} loading={loading}>
            Refresh
          </Button>
        }
      >
        {/* Aggregate Statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Processes"
                value={totalProcesses}
                prefix={<ThunderboltOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Complete"
                value={totalComplete}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Incomplete"
                value={totalProcesses - totalComplete}
                valueStyle={{ color: '#cf1322' }}
                prefix={<WarningOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: 8 }}>
                  Completion Rate
                </div>
                <Progress
                  type="circle"
                  percent={completionPercentage}
                  size={80}
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
              </div>
            </Card>
          </Col>
        </Row>

        {/* Process Type Cards */}
        <Spin spinning={loading}>
          <Row gutter={[16, 16]}>
            {processTypes.map((typeData) => (
              <Col span={8} key={typeData.type}>
                <Card
                  hoverable
                  onClick={() => setExpandedType(expandedType === typeData.type ? null : typeData.type)}
                  style={{ height: '100%' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <Tag color={getProcessTypeColor(typeData.type)} style={{ fontSize: '16px', padding: '4px 8px' }}>
                        {typeData.type}
                      </Tag>
                    </Space>
                    
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: 8 }}>
                      {getProcessTypeDescription(typeData.type)}
                    </div>
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Statistic
                          title="Processes"
                          value={typeData.count}
                          valueStyle={{ fontSize: '24px' }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Complete"
                          value={typeData.complete_count}
                          suffix={`/ ${typeData.count}`}
                          valueStyle={{ fontSize: '20px', color: '#3f8600' }}
                        />
                      </Col>
                    </Row>
                    
                    <Progress
                      percent={typeData.count > 0 ? Math.round((typeData.complete_count / typeData.count) * 100) : 0}
                      status={typeData.incomplete_count === 0 ? 'success' : 'active'}
                      size="small"
                    />
                    
                    {/* Expanded View */}
                    {expandedType === typeData.type && typeData.processes.length > 0 && (
                      <>
                        <Divider style={{ margin: '12px 0' }}>Processes</Divider>
                        <div className={styles.expandedProcessList}>
                        <List
                          size="small"
                          dataSource={typeData.processes}
                          renderItem={(process) => (
                            <List.Item
                              actions={[
                                <Button
                                  key="view"
                                  type="link"
                                  size="small"
                                  icon={<EyeOutlined />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onProcessClick) onProcessClick(process.id);
                                  }}
                                />
                              ]}
                            >
                              <List.Item.Meta
                                title={
                                  <Space>
                                    {process.name}
                                    {process.is_complete ? (
                                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                    ) : (
                                      <WarningOutlined style={{ color: '#faad14' }} />
                                    )}
                                  </Space>
                                }
                                description={`${process.artifact_count} artifacts`}
                              />
                            </List.Item>
                          )}
                        />
                        </div>
                      </>
                    )}
                    
                    {expandedType === typeData.type && typeData.processes.length === 0 && (
                      <div style={{ marginTop: 16, textAlign: 'center', color: '#999' }}>
                        No processes of this type
                      </div>
                    )}
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </Spin>
      </Card>
    </div>
  );
};

